import { CommonFormGrid } from 'components/form/grid';
import { FormHeading } from 'components/form/heading';
import { FormSelect } from 'components/form/select';
import { FormTextField } from 'components/form/textfield';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { OptionDef } from 'interfaces/options';
import { AccountType, isAccountTypeRegistered } from 'lib/enums/account';
import { AccountDoc } from 'lib/interfaces/database/account';
import { useContext, useEffect, useMemo, useState } from 'react';
import useCustodiansStore from 'stores/custodians';
import useInvestorsStore from 'stores/investors';

export const AccountForm = () => {
  const { data: investors, load: loadInvestors } = useInvestorsStore();
  const { data: custodians, load: loadCustodians } = useCustodiansStore();

  useEffect(() => {
    loadInvestors();
    loadCustodians();
  }, []);

  const { data, setData } = useContext(FormContext);

  const [defaultData] = useState<Partial<AccountDoc>>(data);

  const investorOptions = useMemo<OptionDef[]>(
    () =>
      investors.map((m) => ({
        label: m.preferredName,
        // id belongs to the investorPrivate record which is not what we want here
        value: m.investorID,
        group: m.type,
      })),
    [investors]
  );

  const custodianOptions = useMemo<OptionDef[]>(
    () =>
      custodians.map((m) => ({
        label: m.name,
        value: m.id,
      })),
    [custodians]
  );

  return (
    <EB>
      <CommonFormGrid columns="2">
        <FormHeading label="Investor Account" gridColumn="span 2" />

        <FormSelect
          label="Investor"
          defaultValue={defaultData.investorID}
          options={investorOptions}
          onChange={(v) => {
            const payload: Partial<AccountDoc> = {
              investorID: v,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Custodian"
          defaultValue={defaultData.custodianID}
          options={custodianOptions}
          onOptionalChange={(v) => {
            const payload: Partial<AccountDoc> = {
              custodianID: v,
            };
            setData(payload);
          }}
        />

        <FormTextField
          gridColumnStart="1"
          label="Full Identifier"
          defaultValue={defaultData.identifier}
          onChange={(v) => {
            const payload: Partial<AccountDoc> = {
              identifier: v,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Type"
          defaultValue={defaultData.type}
          options={Object.values(AccountType).map((o) => ({
            label: o,
            value: o,
            group: isAccountTypeRegistered(o) ? 'Registered' : 'Non-Registered',
          }))}
          onChange={(v) => {
            const payload: Partial<AccountDoc> = {
              type: v as AccountType,
              registered: isAccountTypeRegistered(v as AccountType),
            };
            setData(payload);
          }}
          hint={
            data.type
              ? isAccountTypeRegistered(data.type)
                ? 'Registered'
                : 'Non-Registered'
              : undefined
          }
          required
        />
      </CommonFormGrid>
    </EB>
  );
};
